import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Button, Badge, EmptyState, Skeleton } from '@/nova/components';
import { createSite, getAffiliateError, listSites } from '../../api';
import { useAffiliateResource } from '../../hooks/useAffiliateResource';
import {
  Section,
  SectionLabel,
  SiteList,
  SiteRow,
  SiteMeta,
  SiteName,
  SiteUrl,
  AddRow,
  AddFields,
} from './SitesSection.styles';

export type SitesSectionProps = {
  casinoInstanceId: string;
  affiliateId: string;
  onToast: (message: string) => void;
};

/** Site-IDs under one affiliate: the list plus an inline add row. Lives in the affiliate drill-down. */
export const SitesSection = ({ casinoInstanceId, affiliateId, onToast }: SitesSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const fetcher = React.useCallback((ci: string) => listSites(ci, affiliateId), [affiliateId]);
  const sites = useAffiliateResource(casinoInstanceId, fetcher);

  const [name, setName] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const canAdd = name.trim().length > 0 && !saving;

  const onAdd = React.useCallback(async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      await createSite(casinoInstanceId, affiliateId, { name: name.trim(), url: url.trim() || undefined });
      setName('');
      setUrl('');
      onToast(t('Site added'));
      sites.reload();
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setSaving(false);
    }
  }, [canAdd, casinoInstanceId, affiliateId, name, url, onToast, t, sites]);

  const rows = sites.data ?? [];

  return (
    <Section>
      <SectionLabel text="Site IDs" tag="span" />

      {sites.loading ? (
        <Skeleton height={40} block />
      ) : rows.length === 0 ? (
        <EmptyState text={t('No site IDs yet')} />
      ) : (
        <SiteList>
          {rows.map((site) => {
            const active = (site.status ?? 'active').toLowerCase() !== 'inactive';
            return (
              <SiteRow key={site.id}>
                <SiteMeta>
                  <SiteName text={site.name} tag="span" />
                  {site.url ? <SiteUrl text={site.url} tag="span" /> : null}
                </SiteMeta>
                <Badge label={active ? t('Active') : t('Inactive')} tone={active ? 'success' : 'neutral'} />
              </SiteRow>
            );
          })}
        </SiteList>
      )}

      <AddRow>
        <AddFields>
          <Field label={t('Site name')} value={name} placeholder={t('Main site')} onChange={setName} />
          <Field label={t('Site URL (optional)')} value={url} placeholder="https://partner.com" onChange={setUrl} />
        </AddFields>
        <Button label={t('Add')} variant="outline" icon="plus" onClick={onAdd} disabled={!canAdd} />
      </AddRow>
    </Section>
  );
};

export default SitesSection;
